/**
 * The contract of a directed video (spec `2026-10-17-music24-directed-video-design.md`): the cast,
 * the stories, the styles, the script of shots and the serialized video. music24-api stores these
 * shapes in the JSON fields of `DirectedVideoModel` and answers them; the web app reads them.
 *
 * Each shape has a hand-written `parse…` that repairs or refuses, like `parseMusicVideoDoc`: unknown
 * keys go, every field is clamped or gets its default, and a value that cannot be repaired is
 * dropped (a list entry) or refused (the script). The director answer and an artist edit go through
 * the same parse. The file has no heavy imports.
 */
import { VIDEO_CREDIT_COST, aiClipCreditsFor } from './music24Plans';
import { MUSIC_VIDEO_BANK_CLIP_SEC } from './musicVideoDoc';
import { parseMusicVideoEffects, type MusicVideoEffect, type MusicVideoEffectFamily } from './musicVideoEffects';

// ---------------------------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------------------------

/** The most cast entries of one directed video. */
export const DIRECTED_MAX_CAST = 8;
/** The most cast entries of one shot: Veo takes at most 3 reference images per clip. */
export const DIRECTED_MAX_CAST_PER_SHOT = 3;
/** The director runs (stories, styles, script, shot rewrites) of one directed video. */
export const DIRECTED_DIRECTOR_RUNS_PER_VIDEO = 30;
/** The director runs of one account in one day, over every directed video. */
export const DIRECTED_DIRECTOR_RUNS_PER_DAY = 60;
/** The generated cast pictures of one directed video. */
export const DIRECTED_CAST_PICTURES_PER_VIDEO = 12;
/**
 * The clip length of a shot that names none (every script from before 2026-10-23, when every clip
 * was one 8 s Veo clip). Every shot plays from one clip.
 */
export const DIRECTED_CLIP_SEC = MUSIC_VIDEO_BANK_CLIP_SEC;
/** The shortest clip of the video model (Wan 3.0 makes 2 to 30 s; shorter clips look unfinished). */
export const DIRECTED_MIN_CLIP_SEC = 3;
/** The longest clip (owner decision 2026-10-23: the director picks the length; a long take is 15 s at most). */
export const DIRECTED_MAX_CLIP_SEC = 15;
/** The shortest shot. A shorter shot goes. */
export const DIRECTED_MIN_SHOT_SEC = 0.25;
/** The most shots of one script. */
export const DIRECTED_MAX_SHOTS = 300;
/** The stories and the styles that the director writes in one run. */
export const DIRECTED_CHOICES = 3;
/** The most beats of one story. */
export const DIRECTED_MAX_STORY_BEATS = 40;
/** The longest label of a cast entry. */
export const DIRECTED_CAST_LABEL_MAX_LENGTH = 80;
/** The longest title of a story or a style. */
export const DIRECTED_TITLE_MAX_LENGTH = 120;
/** The longest short text (a logline, a beat, a style line, a scene, a camera line). */
export const DIRECTED_TEXT_MAX_LENGTH = 600;
/** The longest Veo prompt of a shot (the style text is repeated in every prompt). */
export const DIRECTED_VEO_PROMPT_MAX_LENGTH = 2000;
/** The longest id (a cast entry, a story, a style, a shot, a clip key). */
const MAX_ID_LENGTH = 64;

// ---------------------------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------------------------

/** The steps of a directed video (`DirectedVideoStatus` of the Prisma schema). */
export const DIRECTED_VIDEO_STATUSES = [
  'cast',
  'stories',
  'style',
  'script',
  'priced',
  'making',
  'rendering',
  'ready',
  'failed',
  'cancelled',
] as const;
export type DirectedVideoStatus = (typeof DIRECTED_VIDEO_STATUSES)[number];

export const DIRECTED_CAST_ROLES = ['person', 'place', 'object', 'style'] as const;
export type DirectedCastRole = (typeof DIRECTED_CAST_ROLES)[number];

/** The effect families that a style may ask for. */
const EFFECT_FAMILIES: readonly MusicVideoEffectFamily[] = ['lyric', 'title', 'transition', 'shot'];

/** The longest art style line of a cast description. */
export const DIRECTED_CAST_ART_STYLE_MAX_LENGTH = 160;
/** The longest subject line of a cast description. */
export const DIRECTED_CAST_SUBJECT_MAX_LENGTH = 200;
/** The longest appearance text of a cast description. */
export const DIRECTED_CAST_APPEARANCE_MAX_LENGTH = 600;
/** The most "do not change" items of a cast description. */
export const DIRECTED_CAST_KEEP_MAX = 8;
/** The longest "do not change" item. */
export const DIRECTED_CAST_KEEP_ITEM_MAX_LENGTH = 80;

/**
 * What a cast picture shows, in words (fix 2026-10-18: the director is text only and never sees the
 * picture). A vision model writes it when the picture comes in; the artist can correct it. The
 * director uses it word for word for the look and the art style of the cast entry.
 */
export interface DirectedCastDescription {
  /** The art style of the picture: "photo", "neon line-art illustration on a dark background", ... */
  artStyle: string;
  /** Who or what the picture shows: "a young woman", "a red vintage car". */
  subject: string;
  /** Hair, face, clothes, colours, distinctive marks. */
  appearance: string;
  /** Short items that must stay the same in every shot: "pink wavy hair", "antennae". */
  doNotChange: string[];
}

/** Who wrote the description: the vision model, or the artist (an edit). */
export const DIRECTED_CAST_DESCRIPTION_SOURCES = ['vision', 'artist'] as const;
export type DirectedCastDescriptionSource = (typeof DIRECTED_CAST_DESCRIPTION_SOURCES)[number];

/**
 * The state of the description: `reading` while the vision model reads the picture, `ready` with a
 * description, `failed` when the read failed, `none` for an entry from before the descriptions.
 */
export const DIRECTED_CAST_DESCRIPTION_STATUSES = ['none', 'reading', 'ready', 'failed'] as const;
export type DirectedCastDescriptionStatus = (typeof DIRECTED_CAST_DESCRIPTION_STATUSES)[number];

/** One reference picture of the cast. */
export interface DirectedCastEntry {
  id: string;
  /** The picture (`FileModel.id`) of the account. */
  fileId: number;
  /** A presigned GET, set when the API serializes the entry; empty in the stored JSON. */
  url: string;
  role: DirectedCastRole;
  label: string;
  /** True for a picture that the image model made. */
  generated: boolean;
  /**
   * The user who ticked the consent box for the picture, and when (ISO). Every entry that is not
   * generated (an upload of the artist, of any role) needs both.
   */
  consentUserId: number | null;
  consentAt: string | null;
  /** What the picture shows (null until the vision model read it, or for an old entry). */
  description: DirectedCastDescription | null;
  /** Who wrote `description`; null with no description. */
  descriptionSource: DirectedCastDescriptionSource | null;
  descriptionStatus: DirectedCastDescriptionStatus;
  /**
   * True for a copy of the artist picture of the account (`POST .../cast/artist-picture`, owner
   * request 2026-10-19). The choice of the own picture is the confirmation: the entry stores the
   * user who chose it as `consentUserId` / `consentAt`, and the consent box does not ask for it.
   * Absent on every other entry.
   */
  fromArtistPicture?: true;
}

export interface DirectedStoryBeat {
  /** The section of the song map. */
  sectionIndex: number;
  text: string;
}

/**
 * The most recurring subjects (people, places and objects) of one story. The cast holds
 * `DIRECTED_MAX_CAST` pictures, so a story never asks for more pictures than the cast can take.
 */
export const DIRECTED_MAX_STORY_CHARACTERS = 6;

/**
 * What a recurring subject of a story is (owner request 2026-10-20: objects and places need a
 * reference picture too, so they stay the same from shot to shot). The cast role of its picture.
 */
export const DIRECTED_STORY_CHARACTER_KINDS = ['person', 'place', 'object'] as const;
export type DirectedStoryCharacterKind = (typeof DIRECTED_STORY_CHARACTER_KINDS)[number];
/** The longest role line of a story character. */
export const DIRECTED_CHARACTER_ROLE_MAX_LENGTH = 120;

/**
 * A recurring subject of a story that is not in the cast: a person, a place or an object (fix
 * 2026-10-18: an invented companion with no picture became a different person in every shot; owner
 * request 2026-10-20: the same for a thing that the story moves around and for a place it returns
 * to). `needsPicture`: the subject comes back and must look the same, so the cast needs a picture
 * of it.
 */
export interface DirectedStoryCharacter {
  /** The cast role of its picture. A story from before 2026-10-20 reads as `person`. */
  kind: DirectedStoryCharacterKind;
  name: string;
  role: string;
  needsPicture: boolean;
  /** The look, for a generated cast picture. */
  description: string;
}

export interface DirectedStory {
  id: string;
  title: string;
  logline: string;
  beats: DirectedStoryBeat[];
  /** The recurring people who are not in the cast. Absent on a story from before 2026-10-18. */
  characters?: DirectedStoryCharacter[];
}

export interface DirectedStyle {
  id: string;
  title: string;
  camera: string;
  colourAndLight: string;
  texture: string;
  pacing: string;
  effectFamilies: MusicVideoEffectFamily[];
  /**
   * How the style fits the art style of the cast pictures, as the director judged it. Absent on a
   * style of the artist and on an edited style: then the rule `directedStyleCastFit` decides.
   */
  castFit?: DirectedCastFit;
  /** One line that says why, with `castFit`. */
  castFitReason?: string;
}

/** How a style fits the art style of the cast pictures. */
export const DIRECTED_CAST_FITS = ['good', 'mixed', 'conflict'] as const;
export type DirectedCastFit = (typeof DIRECTED_CAST_FITS)[number];
/** The longest reason line of a cast fit. */
export const DIRECTED_CAST_FIT_REASON_MAX_LENGTH = 200;

export interface DirectedShot {
  id: string;
  /** The place on the song, in seconds. At most `clipSec` long. */
  startSec: number;
  endSec: number;
  scene: string;
  veoPrompt: string;
  /** At most `DIRECTED_MAX_CAST_PER_SHOT` cast entry ids. */
  castIds: string[];
  camera: string;
  /** Shots with the same key share one AI clip. */
  clipKey: string;
  /**
   * The length of the clip, whole seconds from `DIRECTED_MIN_CLIP_SEC` to `DIRECTED_MAX_CLIP_SEC`. Every
   * shot of one clip has the same value (`parseDirectedScript` makes it so). A stored script from
   * before 2026-10-23 has none: it reads as `DIRECTED_CLIP_SEC`.
   */
  clipSec: number;
  /** Where the shot starts inside its clip. The shot always fits in the clip. */
  clipInSec: number;
}

export interface DirectedScript {
  /** Sorted by `startSec`, inside the song. */
  shots: DirectedShot[];
  effects: MusicVideoEffect[];
}

/** The make of a directed video, while it runs. */
export interface DirectedVideoProgress {
  clipsDone: number;
  clipsTotal: number;
  renderPartsDone: number | null;
  renderPartsTotal: number | null;
  etaSec: number | null;
}

/** The clips of a make (also after it: ready and failed show the refund of the failed clips). */
export interface DirectedVideoClipCounts {
  total: number;
  failed: number;
}

/**
 * The rendered file of a make: the kit asset that the render wrote (the music video of the song).
 * The urls are API file paths (`files/<uuid>/download`); the web makes them full. `fileUrl` is null
 * before the render is ready and after the file was removed.
 */
export interface DirectedVideoAsset {
  /** The kit asset uuid: the asset viewer and the "asset is ready" email (`workAssetUrl`) open it. */
  uuid: string;
  status: 'queued' | 'planning' | 'rendering' | 'ready' | 'failed';
  fileUrl: string | null;
  /** The poster frame of the render, else null. */
  posterUrl: string | null;
  durationSec: number | null;
  width: number | null;
  height: number | null;
}

/** The state of one Veo clip of a make (`FootageClipStatus`: `generating` is `making`). */
export const DIRECTED_VIDEO_CLIP_STATUSES = ['queued', 'making', 'ready', 'failed'] as const;
export type DirectedVideoClipStatus = (typeof DIRECTED_VIDEO_CLIP_STATUSES)[number];

/**
 * One Veo clip of a make, in script order (`directedClipKeys`). The make screen shows one tile for
 * each entry.
 */
export interface DirectedVideoClipEntry {
  /** `DirectedShot.clipKey` of the script. */
  clipKey: string;
  /** The shots of the script that play from this clip, as 1-based shot numbers, in order. */
  shotNumbers: number[];
  status: DirectedVideoClipStatus;
  /**
   * A still of the clip (its poster, the frame at 1 s): a 1-hour presigned GET of the clip poster. Null when the clip is not ready or has no poster. Never the cover art of the song.
   */
  stillUrl: string | null;
  /**
   * The keyframe of the clip: the 16:9 still of the image model that Veo animates (the first frame),
   * a 1-hour presigned GET. It exists before the clip is ready, so the tile shows it while Veo makes
   * the clip. Null when the clip has no keyframe (not made yet, or the clip went to Veo with its
   * reference pictures). Absent from an API from before 2026-10-18.
   */
  keyframeUrl?: string | null;
}

/** The longest description of a generated cast picture. */
export const DIRECTED_CAST_DESCRIPTION_MAX_LENGTH = 400;

/**
 * A cast picture that the image model makes now, or that failed. It becomes a cast entry
 * (`generated: true`) when it is ready. The server keeps it about 2 hours.
 */
export interface DirectedCastPicturePending {
  id: string;
  role: DirectedCastRole;
  label: string;
  status: 'working' | 'failed';
  /**
   * The words that the picture was asked with, so "Try again" and "Change the words" start from
   * them. Absent from an API from before 2026-10-20.
   */
  description?: string;
  /** Why a `failed` picture failed (`DIRECTED_CAST_PICTURE_FAIL_REASONS`). Absent while it works. */
  failReason?: DirectedCastPictureFailReason;
}

/**
 * Why the image model did not make a cast picture (owner report 2026-10-20):
 * - `refused`: the safety filter of the image model refused the words. The same words fail again.
 * - `error`: a technical failure. The job tries once more by itself before the card shows it. It
 *   does not use up one of the `DIRECTED_CAST_PICTURES_PER_VIDEO` AI pictures.
 * - `quota`: the AI budget of Music24 is used up for now. It does not use up an AI picture either.
 */
export const DIRECTED_CAST_PICTURE_FAIL_REASONS = ['refused', 'error', 'quota'] as const;
export type DirectedCastPictureFailReason = (typeof DIRECTED_CAST_PICTURE_FAIL_REASONS)[number];

/** True for a fail reason that gives the AI picture back to the video (not a refusal). Pure. */
export function castPictureFailGivesPictureBack(reason: DirectedCastPictureFailReason): boolean {
  return reason !== 'refused';
}

/** The state of the preview still of a style. */
export const DIRECTED_STYLE_PREVIEW_STATUSES = ['working', 'ready', 'failed'] as const;
export type DirectedStylePreviewStatus = (typeof DIRECTED_STYLE_PREVIEW_STATUSES)[number];

/**
 * The preview still of one style of the style step (owner 2026-10-19): one 16:9 picture of the
 * image model that shows the chosen story in the look of the style, with the cast. Free for the
 * artist. The server makes it in the background when the director writes the styles, and when the
 * artist saves an own or an edited style.
 */
export interface DirectedStylePreview {
  /** `DirectedStyle.id` of `styles` or of `style`. */
  styleId: string;
  /** `working`: the image model makes it now (the card shows a spinner). `failed`: no picture. */
  status: DirectedStylePreviewStatus;
  /**
   * The source of the picture: a 1-hour presigned GET of the 1920 × 1080 JPEG. The app makes the
   * imgproxy address for the size it draws. Null unless `ready`.
   */
  imageUrl: string | null;
}

/** One directed video (`DirectedVideoModel`), as the API answers it. */
export interface SerializedDirectedVideo {
  uuid: string;
  status: DirectedVideoStatus;
  cast: DirectedCastEntry[];
  /** The cast pictures that the image model makes now, or that failed in the last 2 hours. */
  castPicturesPending: DirectedCastPicturePending[];
  stories: DirectedStory[] | null;
  story: DirectedStory | null;
  styles: DirectedStyle[] | null;
  style: DirectedStyle | null;
  /**
   * The preview stills of the styles in `styles` and of `style`, for the chosen story and the look
   * that the API holds now. A style with no entry has no preview. Absent from an API from before
   * 2026-10-19.
   */
  stylePreviews?: DirectedStylePreview[];
  script: DirectedScript | null;
  /** +1 on every script write. Confirm sends the revision it read (409 `M24_DIRECTED_STALE`). */
  scriptRevision: number;
  priceCredits: number | null;
  directorState: 'idle' | 'working';
  /** Why the last director run failed (`DIRECTOR_FAILED`, `AI_BUDGET_EXHAUSTED`, `STALLED`, ...). A new run clears it. */
  directorFailCode: string | null;
  directorRunsLeft: number;
  castPicturesLeft: number;
  /** Set in `making` and `rendering`. */
  progress: DirectedVideoProgress | null;
  /** The Veo clips of the make: all of them, and the clips that failed for good. Both 0 before the make. */
  clips: DirectedVideoClipCounts;
  /**
   * One entry for each Veo clip of the make, in script order. Empty before the make. Absent from
   * an API from before 2026-10-18: a client then falls back to `clips` and `progress`.
   */
  clipEntries?: DirectedVideoClipEntry[];
  /** The cut that the make wrote. */
  cutUuid: string | null;
  /**
   * The rendered file of the make, null before the render starts. Absent from an API from before
   * 2026-10-18: a client then shows no player.
   */
  asset?: DirectedVideoAsset | null;
  failCode: string | null;
}

/** What one entry of the history of a directed video holds (`DirectedVideoSnapshotModel.kind`). */
export const DIRECTED_HISTORY_KINDS = [
  'stories',
  'styles',
  'script',
  'shot',
  'directorFailed',
  'storyPick',
  'stylePick',
  'scriptSave',
  'confirm',
] as const;
export type DirectedHistoryKind = (typeof DIRECTED_HISTORY_KINDS)[number];

/**
 * One entry of the history of a directed video ("How it was made", design directed-video l1): a
 * round of the director or a choice of the artist, oldest first. Only the fields of its kind are set.
 * The scripts stay on the server; the page shows the last script of the video.
 */
export interface DirectedHistoryEntry {
  id: number;
  kind: DirectedHistoryKind;
  /** ISO time of the entry. */
  at: string;
  /** The note of the artist for a director run. */
  note: string | null;
  /** `stories`: the 3 ideas of the round. */
  stories?: DirectedStory[];
  /** `styles`: the 3 styles of the round. */
  styles?: DirectedStyle[];
  /** `storyPick`: the picked (maybe edited) story. `styles`, `script`: the story it was written for. */
  story?: DirectedStory | null;
  /** `stylePick`: the picked (maybe edited) style. `script`: the style it was written for. */
  style?: DirectedStyle | null;
  /**
   * `styles`, `stylePick`: the ready preview still of each style of the entry, by style id (a
   * 1-hour presigned GET). A style with no ready preview is not in it.
   */
  stylePreviewUrls?: Record<string, string>;
  /** `shot`: the shot that the director wrote again. */
  shotId?: string;
  /** `script`, `scriptSave`: the number of shots; `confirm`: the shots that went to the make. */
  shotCount?: number;
  /** `directorFailed`: why the run wrote nothing; `step`: the step of the run. */
  failCode?: string | null;
  step?: string;
  /** `confirm`: the price and the clips. */
  priceCredits?: number;
  clipCount?: number;
}

/** `GET .../directed/:uuid/history` → the history, oldest first. Empty for a video from before 2026-10-18. */
export interface DirectedVideoHistoryResponse {
  entries: DirectedHistoryEntry[];
}

/** `GET .../directed` → the directed videos of the song, newest first. */
export interface DirectedVideoListResponse {
  videos: SerializedDirectedVideo[];
}

/** `GET .../directed/:uuid`, and every write → the video. */
export interface DirectedVideoResponse {
  video: SerializedDirectedVideo;
}

/**
 * `GET .../directed/:uuid/price` (spec §7.1, design f1): the AI clips of the script, their seconds of
 * new footage, the price (`directedPriceCredits`: each clip by its length), the script revision of the
 * price (confirm sends it), and the video credits the workspace has now (plan left plus packs; null =
 * no limit).
 */
export interface DirectedVideoPriceResponse {
  clipCount: number;
  /** The lengths of the clips added up (`directedFootageSec`). Absent from a server before 2026-10-23. */
  footageSec?: number;
  priceCredits: number;
  scriptRevision: number;
  balance: number | null;
}

/** Why a script was refused. */
export type DirectedScriptRefusal = 'duration' | 'notObject' | 'shots' | 'tooManyShots' | 'noShots';

export type ParseDirectedScriptResult =
  | { ok: true; script: DirectedScript }
  | { ok: false; reason: DirectedScriptRefusal };

// ---------------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Seconds kept to the millisecond. */
function roundSec(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function idOf(value: unknown): string | null {
  return typeof value === 'string' && value !== '' && value.length <= MAX_ID_LENGTH ? value : null;
}

function textOf(value: unknown, maxLength: number): string {
  return typeof value === 'string' ? value.trim().slice(0, maxLength) : '';
}

function positiveIntOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

export function isDirectedCastRole(value: unknown): value is DirectedCastRole {
  return typeof value === 'string' && (DIRECTED_CAST_ROLES as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------------------------
// Cast
// ---------------------------------------------------------------------------------------------

/** One cast entry, or null when it cannot be repaired. */
export function parseDirectedCastEntry(value: unknown): DirectedCastEntry | null {
  if (!isRecord(value)) {
    return null;
  }
  const id = idOf(value.id);
  const fileId = positiveIntOf(value.fileId);
  const { role } = value;
  if (id === null || fileId === null || !isDirectedCastRole(role)) {
    return null;
  }
  const generated = value.generated === true;
  const description = parseDirectedCastDescription(value.description);
  const consentUserId = positiveIntOf(value.consentUserId);
  const consentAt = typeof value.consentAt === 'string' && value.consentAt !== '' ? value.consentAt : null;
  const hasConsent = consentUserId !== null && consentAt !== null;
  // An upload of any role can show a person, so every upload needs the consent tick (review I3).
  if (!generated && !hasConsent) {
    return null;
  }
  return {
    id,
    fileId,
    url: typeof value.url === 'string' ? value.url : '',
    role,
    label: textOf(value.label, DIRECTED_CAST_LABEL_MAX_LENGTH),
    generated,
    consentUserId: hasConsent ? consentUserId : null,
    consentAt: hasConsent ? consentAt : null,
    description,
    descriptionSource: description === null ? null : descriptionSourceOf(value.descriptionSource),
    descriptionStatus: descriptionStatusOf(description, value.descriptionStatus),
    ...(value.fromArtistPicture === true ? { fromArtistPicture: true as const } : {}),
  };
}

function descriptionSourceOf(value: unknown): DirectedCastDescriptionSource {
  return value === 'artist' ? 'artist' : 'vision';
}

/** `ready` with a description; a stored `reading` or `failed` without one; else `none`. */
function descriptionStatusOf(
  description: DirectedCastDescription | null,
  value: unknown
): DirectedCastDescriptionStatus {
  if (description !== null) {
    return 'ready';
  }
  return value === 'reading' || value === 'failed' ? value : 'none';
}

/**
 * A cast description with trimmed, clamped fields, or null when it is not an object or has no
 * subject, no appearance and no art style. Empty "do not change" items and repeats go. Pure.
 */
export function parseDirectedCastDescription(value: unknown): DirectedCastDescription | null {
  if (!isRecord(value)) {
    return null;
  }
  const artStyle = textOf(value.artStyle, DIRECTED_CAST_ART_STYLE_MAX_LENGTH);
  const subject = textOf(value.subject, DIRECTED_CAST_SUBJECT_MAX_LENGTH);
  const appearance = textOf(value.appearance, DIRECTED_CAST_APPEARANCE_MAX_LENGTH);
  if (artStyle === '' && subject === '' && appearance === '') {
    return null;
  }
  const rawKeep: unknown[] = Array.isArray(value.doNotChange) ? value.doNotChange : [];
  const doNotChange: string[] = [];
  for (const raw of rawKeep) {
    const item = textOf(raw, DIRECTED_CAST_KEEP_ITEM_MAX_LENGTH);
    if (item === '' || doNotChange.some((kept) => kept.toLowerCase() === item.toLowerCase())) {
      continue;
    }
    doNotChange.push(item);
    if (doNotChange.length >= DIRECTED_CAST_KEEP_MAX) {
      break;
    }
  }
  return { artStyle, subject, appearance, doNotChange };
}

/**
 * The cast of a directed video. A bad entry, a second entry with the same id and every entry after
 * `DIRECTED_MAX_CAST` go. A picture that is not generated and has no consent goes (any role). Not a
 * list: an empty cast.
 */
export function parseDirectedCast(input: unknown): DirectedCastEntry[] {
  if (!Array.isArray(input)) {
    return [];
  }
  const out: DirectedCastEntry[] = [];
  const ids = new Set<string>();
  for (const raw of input) {
    const entry = parseDirectedCastEntry(raw);
    if (entry === null || ids.has(entry.id)) {
      continue;
    }
    ids.add(entry.id);
    out.push(entry);
    if (out.length >= DIRECTED_MAX_CAST) {
      break;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Stories and styles
// ---------------------------------------------------------------------------------------------

/** One story, or null when it has no id or no title. Beats with a bad section or no text go. */
export function parseDirectedStory(value: unknown): DirectedStory | null {
  if (!isRecord(value)) {
    return null;
  }
  const id = idOf(value.id);
  const title = textOf(value.title, DIRECTED_TITLE_MAX_LENGTH);
  if (id === null || title === '') {
    return null;
  }
  const rawBeats: unknown[] = Array.isArray(value.beats) ? value.beats : [];
  const beats: DirectedStoryBeat[] = [];
  for (const raw of rawBeats) {
    if (!isRecord(raw)) {
      continue;
    }
    const { sectionIndex } = raw;
    const text = textOf(raw.text, DIRECTED_TEXT_MAX_LENGTH);
    if (typeof sectionIndex !== 'number' || !Number.isInteger(sectionIndex) || sectionIndex < 0 || text === '') {
      continue;
    }
    beats.push({ sectionIndex, text });
    if (beats.length >= DIRECTED_MAX_STORY_BEATS) {
      break;
    }
  }
  beats.sort((a, b) => a.sectionIndex - b.sectionIndex);
  return {
    id,
    title,
    logline: textOf(value.logline, DIRECTED_TEXT_MAX_LENGTH),
    beats,
    characters: parseStoryCharacters(value.characters),
  };
}

/** The characters of a story: a name each, unique names, at most `DIRECTED_MAX_STORY_CHARACTERS`. */
function parseStoryCharacters(input: unknown): DirectedStoryCharacter[] {
  if (!Array.isArray(input)) {
    return [];
  }
  const out: DirectedStoryCharacter[] = [];
  for (const raw of input) {
    if (!isRecord(raw)) {
      continue;
    }
    const name = textOf(raw.name, DIRECTED_CAST_LABEL_MAX_LENGTH);
    if (name === '' || out.some((character) => character.name.toLowerCase() === name.toLowerCase())) {
      continue;
    }
    out.push({
      kind: DIRECTED_STORY_CHARACTER_KINDS.includes(raw.kind as DirectedStoryCharacterKind)
        ? (raw.kind as DirectedStoryCharacterKind)
        : 'person',
      name,
      role: textOf(raw.role, DIRECTED_CHARACTER_ROLE_MAX_LENGTH),
      needsPicture: raw.needsPicture !== false,
      description: textOf(raw.description, DIRECTED_CAST_DESCRIPTION_MAX_LENGTH),
    });
    if (out.length >= DIRECTED_MAX_STORY_CHARACTERS) {
      break;
    }
  }
  return out;
}

/** One style, or null when it has no id or no title. Unknown effect families go. */
export function parseDirectedStyle(value: unknown): DirectedStyle | null {
  if (!isRecord(value)) {
    return null;
  }
  const id = idOf(value.id);
  const title = textOf(value.title, DIRECTED_TITLE_MAX_LENGTH);
  if (id === null || title === '') {
    return null;
  }
  const rawFamilies: unknown[] = Array.isArray(value.effectFamilies) ? value.effectFamilies : [];
  const effectFamilies = EFFECT_FAMILIES.filter((family) => rawFamilies.includes(family));
  const style: DirectedStyle = {
    id,
    title,
    camera: textOf(value.camera, DIRECTED_TEXT_MAX_LENGTH),
    colourAndLight: textOf(value.colourAndLight, DIRECTED_TEXT_MAX_LENGTH),
    texture: textOf(value.texture, DIRECTED_TEXT_MAX_LENGTH),
    pacing: textOf(value.pacing, DIRECTED_TEXT_MAX_LENGTH),
    effectFamilies,
  };
  const { castFit } = value;
  if (typeof castFit === 'string' && (DIRECTED_CAST_FITS as readonly string[]).includes(castFit)) {
    style.castFit = castFit as DirectedCastFit;
    style.castFitReason = textOf(value.castFitReason, DIRECTED_CAST_FIT_REASON_MAX_LENGTH);
  }
  return style;
}

/** True when two styles have the same look texts (the fields that the artist edits). Pure. */
export function sameDirectedStyleLook(a: DirectedStyle, b: DirectedStyle): boolean {
  return (
    a.title === b.title &&
    a.camera === b.camera &&
    a.colourAndLight === b.colourAndLight &&
    a.texture === b.texture &&
    a.pacing === b.pacing
  );
}

function parseChoices<T extends { id: string }>(input: unknown, parseOne: (value: unknown) => T | null): T[] {
  if (!Array.isArray(input)) {
    return [];
  }
  const out: T[] = [];
  const ids = new Set<string>();
  for (const raw of input) {
    const choice = parseOne(raw);
    if (choice === null || ids.has(choice.id)) {
      continue;
    }
    ids.add(choice.id);
    out.push(choice);
    if (out.length >= DIRECTED_CHOICES) {
      break;
    }
  }
  return out;
}

/** The stories of a director run: at most `DIRECTED_CHOICES`, unique ids, bad ones dropped. */
export function parseDirectedStories(input: unknown): DirectedStory[] {
  return parseChoices(input, parseDirectedStory);
}

/** The styles of a director run: at most `DIRECTED_CHOICES`, unique ids, bad ones dropped. */
export function parseDirectedStyles(input: unknown): DirectedStyle[] {
  return parseChoices(input, parseDirectedStyle);
}

/**
 * The style text of a directed video: the lines of the style in one sentence list. It goes into
 * every Veo prompt of the video and into a referenced new shot of the editor. Pure.
 */
export function directedStyleText(style: DirectedStyle): string {
  return [style.camera, style.colourAndLight, style.texture]
    .map((line) => line.trim())
    .filter((line) => line !== '')
    .join(' ');
}

/** FNV-1a (32 bit) of a text, with a seed, as 8 hex digits. Pure. */
/* eslint-disable no-bitwise -- FNV-1a is 32-bit integer arithmetic */
function fnv1aHex(text: string, seed: number): string {
  let hash = seed >>> 0;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}
/* eslint-enable no-bitwise */

/**
 * The key of the preview still of a style (`DirectedStylePreview`): the story that it shows and the
 * look texts of the style. An edit of the look or a new story gives a new key, so an old picture
 * never shows for a new look. Pure.
 */
export function directedStylePreviewKey(story: DirectedStory | null, style: DirectedStyle): string {
  const text = [
    story?.id ?? '',
    story?.title ?? '',
    story?.logline ?? '',
    style.title,
    style.camera,
    style.colourAndLight,
    style.texture,
    style.pacing,
  ].join('\u0000');
  return `${fnv1aHex(text, 0x811c9dc5)}${fnv1aHex(text, 0x2166136b)}`;
}

// ---------------------------------------------------------------------------------------------
// Script
// ---------------------------------------------------------------------------------------------

/** One shot with clamped fields, or null when it cannot be repaired or lies outside the song. */
function parseShot(value: unknown, songEnd: number, castIds: ReadonlySet<string> | null): DirectedShot | null {
  if (!isRecord(value)) {
    return null;
  }
  const id = idOf(value.id);
  if (id === null) {
    return null;
  }
  const rawStart = finiteOr(value.startSec, Number.NaN);
  const rawEnd = finiteOr(value.endSec, Number.NaN);
  if (Number.isNaN(rawStart) || Number.isNaN(rawEnd) || rawStart >= songEnd || rawEnd <= 0) {
    return null;
  }
  const startSec = roundSec(clamp(rawStart, 0, songEnd));
  const endSec = roundSec(clamp(rawEnd, startSec, Math.min(songEnd, startSec + DIRECTED_MAX_CLIP_SEC)));
  const lengthSec = endSec - startSec;
  if (lengthSec < DIRECTED_MIN_SHOT_SEC) {
    return null;
  }
  // The clip holds the shot: a clip shorter than the shot grows to it.
  const clipSec = Math.max(directedClipSecOf(value.clipSec), directedClipSecOf(Math.ceil(lengthSec - 1e-9)));

  const rawCast: unknown[] = Array.isArray(value.castIds) ? value.castIds : [];
  const shotCast: string[] = [];
  for (const raw of rawCast) {
    const castId = idOf(raw);
    if (castId === null || shotCast.includes(castId) || (castIds !== null && !castIds.has(castId))) {
      continue;
    }
    shotCast.push(castId);
    if (shotCast.length >= DIRECTED_MAX_CAST_PER_SHOT) {
      break;
    }
  }

  return {
    id,
    startSec,
    endSec,
    scene: textOf(value.scene, DIRECTED_TEXT_MAX_LENGTH),
    veoPrompt: textOf(value.veoPrompt, DIRECTED_VEO_PROMPT_MAX_LENGTH),
    castIds: shotCast,
    camera: textOf(value.camera, DIRECTED_TEXT_MAX_LENGTH),
    clipKey: idOf(value.clipKey) ?? id,
    clipSec,
    clipInSec: roundSec(clamp(finiteOr(value.clipInSec, 0), 0, clipSec - lengthSec)),
  };
}

/**
 * A clip length: a whole number of seconds from `DIRECTED_MIN_CLIP_SEC` to `DIRECTED_MAX_CLIP_SEC`. No
 * number (a script from before 2026-10-23): `DIRECTED_CLIP_SEC`. Pure.
 */
export function directedClipSecOf(value: unknown): number {
  const raw = finiteOr(value, DIRECTED_CLIP_SEC);
  return clamp(Math.round(raw), DIRECTED_MIN_CLIP_SEC, DIRECTED_MAX_CLIP_SEC);
}

/**
 * Every shot of one clip gets the longest `clipSec` of the shots of that clip (they play from one
 * clip of one length); `clipInSec` stays inside it. Pure.
 */
export function unifyDirectedClipSec(shots: DirectedShot[]): DirectedShot[] {
  const longest = new Map<string, number>();
  for (const shot of shots) {
    longest.set(shot.clipKey, Math.max(longest.get(shot.clipKey) ?? 0, shot.clipSec));
  }
  return shots.map((shot) => {
    const clipSec = longest.get(shot.clipKey) ?? shot.clipSec;
    const lengthSec = shot.endSec - shot.startSec;
    return clipSec === shot.clipSec
      ? shot
      : { ...shot, clipSec, clipInSec: roundSec(clamp(shot.clipInSec, 0, Math.max(0, clipSec - lengthSec))) };
  });
}

/**
 * Checks a script from the director or from the artist against a song of `durationSec` seconds.
 * `castIds`: the ids of the cast; a shot keeps only these (no list: any id).
 * - A shot with no id, no times, a second use of an id, or a place outside the song goes.
 * - A shot that runs past the song end ends there; a shot longer than `DIRECTED_MAX_CLIP_SEC` is cut to it.
 * - A shot keeps its first `DIRECTED_MAX_CAST_PER_SHOT` cast ids.
 * - `clipSec` is a whole number from `DIRECTED_MIN_CLIP_SEC` to `DIRECTED_MAX_CLIP_SEC` (none: 8), at least
 *   the shot length, and the same for every shot of one clip (the longest). `clipInSec` is capped so
 *   the shot fits in its clip. A shot with no `clipKey` gets its own id.
 * - The shots are sorted by `startSec` (equal starts keep the input order).
 * - The effects are repaired with `parseMusicVideoEffects`.
 * Overlaps and gaps stay: the cut builder makes the clips cover the song (`normaliseMusicVideoClips`).
 * Refused: not an object, `shots` not a list, more than `DIRECTED_MAX_SHOTS`, no shot left.
 */
export function parseDirectedScript(
  input: unknown,
  options: { durationSec: number; castIds?: readonly string[] }
): ParseDirectedScriptResult {
  const { durationSec } = options;
  if (!Number.isFinite(durationSec) || durationSec <= 0) {
    return { ok: false, reason: 'duration' };
  }
  if (!isRecord(input)) {
    return { ok: false, reason: 'notObject' };
  }
  if (!Array.isArray(input.shots)) {
    return { ok: false, reason: 'shots' };
  }
  const rawShots: unknown[] = input.shots;
  if (rawShots.length > DIRECTED_MAX_SHOTS) {
    return { ok: false, reason: 'tooManyShots' };
  }
  const songEnd = roundSec(durationSec);
  const castIds = options.castIds === undefined ? null : new Set(options.castIds);
  const shots: DirectedShot[] = [];
  const ids = new Set<string>();
  for (const raw of rawShots) {
    const shot = parseShot(raw, songEnd, castIds);
    if (shot === null || ids.has(shot.id)) {
      continue;
    }
    ids.add(shot.id);
    shots.push(shot);
  }
  if (shots.length === 0) {
    return { ok: false, reason: 'noShots' };
  }
  const sorted = shots
    .map((shot, index) => ({ shot, index }))
    .sort((a, b) => {
      const byStart = a.shot.startSec - b.shot.startSec;
      return byStart !== 0 ? byStart : a.index - b.index;
    })
    .map(({ shot }) => shot);
  return {
    ok: true,
    script: { shots: unifyDirectedClipSec(sorted), effects: parseMusicVideoEffects(input.effects, durationSec) },
  };
}

/** The unique clip keys of the script, in shot order: one AI clip each. Pure. */
export function directedClipKeys(script: DirectedScript): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const shot of script.shots) {
    if (!seen.has(shot.clipKey)) {
      seen.add(shot.clipKey);
      keys.push(shot.clipKey);
    }
  }
  return keys;
}

/** The clips of the script in shot order, each with its length (the `clipSec` of its shots). Pure. */
export function directedClips(script: DirectedScript): Array<{ clipKey: string; clipSec: number }> {
  const lengths = new Map<string, number>();
  for (const shot of script.shots) {
    lengths.set(shot.clipKey, Math.max(lengths.get(shot.clipKey) ?? 0, directedClipSecOf(shot.clipSec)));
  }
  return directedClipKeys(script).map((clipKey) => ({ clipKey, clipSec: lengths.get(clipKey) ?? DIRECTED_CLIP_SEC }));
}

/** The seconds of new footage of the script: the lengths of its clips added up. Pure. */
export function directedFootageSec(script: DirectedScript): number {
  return directedClips(script).reduce((sum, clip) => sum + clip.clipSec, 0);
}

/**
 * The price of the make: the credits of each clip by its length (`aiClipCreditsFor`: 0.75 a second,
 * rounded up for each clip; 6 for an 8 s clip). The render is included, so the price is never below
 * the price of a full-song render (`VIDEO_CREDIT_COST.fullSong`, review M6). Pure.
 */
export function directedPriceCredits(script: DirectedScript): number {
  const clips = directedClips(script).reduce((sum, clip) => sum + aiClipCreditsFor(clip.clipSec), 0);
  return Math.max(clips, VIDEO_CREDIT_COST.fullSong);
}

/**
 * The credits back for the failed clips of a confirmed make: each failed clip gets its share of the
 * price by its seconds (`priceCredits × clipSec / footage seconds`, rounded down), never more than
 * the price. A make of equal clips (every make from before 2026-10-23: 8 s each) gives
 * `priceCredits / clips` per failed clip, so a video confirmed at an older price gets that price
 * back. No stored price: `aiClipCreditsFor` of each failed clip. Pure.
 */
export function directedRefundCredits(
  priceCredits: number | null,
  clips: ReadonlyArray<{ clipSec: number; failed: boolean }>
): number {
  const failed = clips.filter((clip) => clip.failed);
  if (failed.length === 0) {
    return 0;
  }
  const totalSec = clips.reduce((sum, clip) => sum + clip.clipSec, 0);
  if (priceCredits === null || totalSec <= 0) {
    return failed.reduce((sum, clip) => sum + aiClipCreditsFor(clip.clipSec), 0);
  }
  const back = failed.reduce((sum, clip) => sum + Math.floor((priceCredits * clip.clipSec) / totalSec + 1e-9), 0);
  return Math.min(back, priceCredits);
}
